import { useState, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "@salesapp/ui";
import { useColumnResize } from "./useColumnResize";

export type DataColumn = {
  key: string;
  label: string;
  /** Shown in the collapsed mobile summary strip. First primary is the title. */
  primary?: boolean;
  /** Optional info-mark tooltip on the column header. */
  hint?: string;
  /** Enables the sort toggle on this column's header. */
  sortable?: boolean;
  /** Plain-text value to sort/filter by (cells may hold rich ReactNode). Defaults to the rendered cell when omitted. */
  sortValue?: (row: DataRow) => string | number;
  /** Offers a "Filter by <label>" dropdown restricted to these values (usually distinct cell values). */
  filterOptions?: string[];
  /** Plain-text value to match against the active filter for this column. Defaults to sortValue, then the rendered cell. */
  filterValue?: (row: DataRow) => string;
};

export type DataRow = {
  id: string;
  cells: Record<string, ReactNode>;
  /** Navigate here when the mobile card is activated (detail pages). */
  href?: string;
  /** Prefer this over href when present (e.g. products → edit). */
  onOpen?: () => void;
  actions?: ReactNode;
  /** Extra row class (e.g. muted converted/won rows). */
  className?: string;
};

type SortState = { key: string; direction: "asc" | "desc" } | null;

function cellText(row: DataRow, key: string): string {
  const value = row.cells[key];
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "";
}

function compareValues(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Desktop: normal table. Mobile: key-info cards; tap opens the record
 * (href/onOpen) or expands the remaining fields when no open handler exists.
 * Columns marked `sortable` get a clickable header toggle; columns with
 * `filterOptions` get a header dropdown that narrows the visible rows.
 */
export function ResponsiveDataTable({
  columns,
  rows,
  caption,
}: {
  columns: DataColumn[];
  rows: DataRow[];
  caption?: string;
}) {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const resize = useColumnResize();
  const primaryCols = columns.filter((col) => col.primary);
  const summaryKeys = new Set(
    (primaryCols.length > 0 ? primaryCols : columns.slice(0, 2)).map((col) => col.key),
  );
  const titleKey = (primaryCols[0] ?? columns[0])?.key;

  function toggleSort(col: DataColumn) {
    if (!col.sortable) return;
    setSort((current) => {
      if (!current || current.key !== col.key) return { key: col.key, direction: "asc" };
      if (current.direction === "asc") return { key: col.key, direction: "desc" };
      return null;
    });
  }

  let visibleRows = rows;
  for (const col of columns) {
    const activeFilter = filters[col.key];
    if (col.filterOptions && activeFilter) {
      const valueOf = col.filterValue ?? ((row: DataRow) => String(col.sortValue?.(row) ?? cellText(row, col.key)));
      visibleRows = visibleRows.filter((row) => valueOf(row) === activeFilter);
    }
  }

  if (sort) {
    const sortCol = columns.find((col) => col.key === sort.key);
    if (sortCol) {
      const valueOf = sortCol.sortValue ?? ((row: DataRow) => cellText(row, sortCol.key));
      visibleRows = [...visibleRows].sort((a, b) => {
        const result = compareValues(valueOf(a), valueOf(b));
        return sort.direction === "asc" ? result : -result;
      });
    }
  }

  function activate(row: DataRow) {
    if (row.onOpen) {
      row.onOpen();
      return;
    }
    if (row.href) {
      navigate(row.href);
      return;
    }
    setExpandedId((current) => (current === row.id ? null : row.id));
  }

  function onRowKey(event: KeyboardEvent<HTMLTableRowElement>, row: DataRow) {
    if (event.target !== event.currentTarget) return; // keys on an inner link/button belong to that control
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      activate(row);
    }
  }

  return (
    <table className="crm-table crm-table--responsive crm-table--resizable" aria-label={caption} style={resize.tableProps.style}>
      <thead>
        <tr ref={resize.headRef}>
          {columns.map((col) => {
            const label = col.hint ? (
              <Tooltip text={col.hint} label={`About ${col.label}`}>
                <span>{col.label}</span>
              </Tooltip>
            ) : (
              col.label
            );
            const isSorted = sort?.key === col.key;
            const colIndex = columns.indexOf(col);
            return (
              <th
                key={col.key}
                {...resize.thProps(colIndex)}
                aria-sort={col.sortable ? (isSorted ? (sort.direction === "asc" ? "ascending" : "descending") : "none") : undefined}
              >
                <div className="crm-table-header-cell">
                  {col.sortable ? (
                    <button
                      type="button"
                      className="crm-sort-btn"
                      onClick={() => toggleSort(col)}
                      title={`Sort by ${col.label}`}
                    >
                      {label}
                    </button>
                  ) : (
                    label
                  )}
                  {col.filterOptions && (
                    <select
                      className="crm-select crm-table-filter-select"
                      aria-label={`Filter by ${col.label}`}
                      title={`Filter by ${col.label}`}
                      value={filters[col.key] ?? ""}
                      onChange={(event) =>
                        setFilters((current) => ({ ...current, [col.key]: event.target.value }))
                      }
                    >
                      <option value="">All</option>
                      {col.filterOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {resize.handle(colIndex)}
              </th>
            );
          })}
          <th
            className="crm-table-actions-col"
            title="Row actions for this record"
            {...resize.thProps(columns.length)}
          >
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {visibleRows.map((row) => {
          const expanded = expandedId === row.id;
          const canOpen = Boolean(row.href || row.onOpen);
          return (
            <tr
              key={row.id}
              className={[
                "crm-table-row",
                canOpen ? "is-openable" : "is-expandable",
                expanded ? "is-expanded" : "",
                row.className ?? "",
              ]
                .filter(Boolean)
                .join(" ")}
              tabIndex={0}
              aria-expanded={!canOpen ? expanded : undefined}
              onClick={(event) => {
                // A click on the row's own link or button is handled by that control. Navigating here too
                // pushed the same URL twice (Back needed two presses) and hijacked ctrl/cmd-click.
                if ((event.target as HTMLElement).closest("a, button, input, select, textarea, label")) return;
                activate(row);
              }}
              onKeyDown={(event) => onRowKey(event, row)}
            >
              {columns.map((col) => {
                const isSummary = summaryKeys.has(col.key);
                const className = [
                  isSummary ? "crm-table-cell--summary" : "crm-table-cell--detail",
                  col.key === titleKey ? "crm-table-cell--title" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <td key={col.key} data-label={col.label} className={className}>
                    {row.cells[col.key] ?? "—"}
                  </td>
                );
              })}
              <td
                className="crm-table-actions"
                data-label="Actions"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <span className="crm-table-actions-group">{row.actions}</span>
                {!canOpen ? (
                  <button
                    type="button"
                    className="crm-table-expand-btn"
                    aria-expanded={expanded}
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpandedId((current) => (current === row.id ? null : row.id));
                    }}
                  >
                    {expanded ? "Hide details" : "Details"}
                  </button>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
